"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { startRealtime } from "@/lib/realtime/live";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Turns the server's change notifications into refetches.
 *
 * The stream says which collection changed; this says which screens care. Every
 * query holding that collection is invalidated, and react-query refetches the
 * ones that are actually mounted — so a booking made on someone else's phone
 * appears here without a reload, and a screen nobody is looking at costs
 * nothing.
 */
const FED_BY: Record<string, string[]> = {
  // A plan changed in Stripe lands here: what it allows, what it costs, and
  // the card and invoices behind it, and whether its last payment failed.
  plan_snapshots: [
    "plan-snapshot",
    "billing-payment-alert",
    "billing-overview",
    "billing-usage",
    "billing-plans",
    "billing-payment-method",
    "billing-invoices",
  ],
  // Calendar tiles and the dashboard name the customer, and usage counts them.
  // A message panel's "nobody to send that to" is about the customer too, so
  // an address or number added lets the message go out without a reopen.
  customers: [
    "customers",
    "customer",
    "customer-performance",
    "customer-events",
    "customer-activity",
    "calendar-events",
    "event-notifications",
    "session-notifications",
    "dashboard",
    "dashboard-setup",
    "billing-usage",
  ],
  services: [
    "services",
    "service",
    "service-performance",
    "service-events",
    "calendar-events",
    "free-times",
    "dashboard",
    "dashboard-setup",
  ],
  service_categories: ["service-categories"],
  class_types: ["class-types", "class-type", "calendar-classes", "dashboard"],
  class_schedules: ["class-schedules", "class-schedule"],
  // A sitting written out by the server, or one that has just filled up.
  class_sessions: [
    "calendar-classes",
    "free-times",
    "class-session",
    "class-type-sessions",
    "class-performance",
    // Customer and team figures count class places (MANIT-1157).
    "customer-performance",
    "team-performance",
    "session-notifications",
    "dashboard",
  ],
  // A place taken or given up moves the count on the session beside it.
  class_bookings: [
    "session-bookings",
    "customer-class-bookings",
    "class-session",
    "calendar-classes",
    "class-performance",
    "customer-performance",
    "team-performance",
    "session-notifications",
    "dashboard",
  ],
  waitlist_entries: ["session-waitlist", "class-session", "calendar-classes"],
  appointments: [
    "calendar-events",
    "free-times",
    "customer-events",
    "service-events",
    "customer-performance",
    "service-performance",
    "team-performance",
    "dashboard",
    "dashboard-setup",
    "event-notifications",
    "billing-usage",
    "billing-usage-history",
  ],
  // Tiles and the dashboard show who the work is with; usage counts seats.
  // Somebody arriving is somebody the team was waiting on, so the pending
  // invitations are asked again with the list they joined.
  users: [
    "my-details",
    "team-members",
    "invitations",
    "team-member",
    "calendar-events",
    "calendar-classes",
    "dashboard",
    "dashboard-setup",
    "billing-usage",
  ],
  availability: ["business-hours", "member-schedule", "days-off", "team-availability", "closures", "free-times"],
  business_profile: ["business-profile", "settings", "tenant-logo"],
  booking_settings: ["booking-settings", "free-times"],
  notification_settings: ["notification-settings"],
  audit_log: ["activity", "customer-activity"],
};

/**
 * Recurring appointments and class sessions are expanded server-side, which
 * emits one event per row created. Invalidating on each would fire a refetch
 * per occurrence for what the user did once, so they are collected and flushed
 * together.
 */
const FLUSH_MS = 300;

/**
 * Rewriting a series streams for seconds, not milliseconds. Waiting only for a
 * gap would hold the first change back for as long as the writing went on, so
 * the collection is flushed at least this often while it keeps arriving.
 */
const FLUSH_LATEST_MS = 1500;

export function RealtimeUpdates() {
  const queryClient = useQueryClient();
  // The stream belongs to one business's server, so switching business has to
  // tear it down: the old one would keep reporting the previous business.
  const tenantId = useAuthStore((state) => state.tenant?.id ?? null);
  const signedIn = useAuthStore((state) => state.status === "authenticated");
  // The profile is set once a session is open on the business's own server.
  // Subscribing before then opens the stream on whatever server the client
  // last pointed at, on a first sign-in MainApp, and it stays there.
  const sessionReady = useAuthStore((state) => state.profile !== null);

  useEffect(() => {
    if (!tenantId || !signedIn || !sessionReady) return;

    const pending = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stop: (() => void) | null = null;
    let cancelled = false;

    let firstAt = 0;

    const flush = () => {
      timer = null;
      firstAt = 0;
      for (const key of pending) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      pending.clear();
    };

    void startRealtime((collection) => {
      const keys = FED_BY[collection];
      // Collections the dashboard does not draw. Nothing to refetch.
      if (!keys) return;
      for (const key of keys) pending.add(key);
      // Timed from the last change rather than the first, so a series being
      // rewritten is drawn once it has settled instead of once every window
      // while it is still being written.
      const now = Date.now();
      if (!firstAt) firstAt = now;
      if (timer) clearTimeout(timer);
      timer = setTimeout(
        flush,
        Math.min(FLUSH_MS, Math.max(0, firstAt + FLUSH_LATEST_MS - now)),
      );
    }).then((unsubscribe) => {
      // The effect can be torn down while the subscriptions are still opening.
      if (cancelled) unsubscribe();
      else stop = unsubscribe;
    });

    return () => {
      cancelled = true;
      stop?.();
      if (timer) clearTimeout(timer);
    };
  }, [queryClient, tenantId, signedIn, sessionReady]);

  return null;
}
